import { useState, useEffect, type CSSProperties } from "react";
import type {
  EntityTypeDefinition,
  FieldDefinition,
  StateDefinition,
  TransitionDefinition,
  WorkflowDefinition,
} from "../../../lib/types";
import { useUpdateEntityType } from "../../../lib/hooks/use-kuvio";
import { showToast } from "../primitives";
import { ConfirmDeleteWorkflowModal } from "./ConfirmDeleteWorkflowModal";

/**
 * Sprint 14b — self-contained workflow editor. The editor OWNS its backing enum
 * field end-to-end: you name the field (immutable once created), define the
 * states (their slugs ARE the field's enum choices), set the initial state, and
 * one Save issues a single update_entity_type carrying both the field and the
 * `workflow`. Deleting the workflow also drops that field. Linear transitions are
 * auto-derived from list order plus an optional reverse-to-previous-active step;
 * guards/effects and non-linear branches stay MCP-only (Sprint 14a Q3A).
 */

interface EditorState {
  id: string;
  slug: string;
  label: string;
  category: "active" | "terminal";
  allowReverse: boolean;
  slugEdited: boolean;
  originalSlug: string | null; // present in the loaded workflow → slug edits warn
}

let _sid = 0;
function nextId(): string {
  return `wfs_${++_sid}`;
}

function slugify(input: string): string {
  return input.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
}

/** Field names the platform owns — a workflow field can't shadow these. */
const RESERVED_FIELD_NAMES = ["id", "created_at", "updated_at", "archived_at"];

/**
 * Validate the workflow field name on initial create (immutable afterwards).
 * Returns an error string, or null when valid. Mirrors the slug convention used
 * elsewhere (lowercase letters/numbers/underscore, must start with a letter).
 */
function validateFieldName(name: string, existingFields: FieldDefinition[]): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Field name is required.";
  if (!/^[a-z][a-z0-9_]*$/.test(trimmed))
    return "Use lowercase letters, numbers, and underscores (start with a letter).";
  if (RESERVED_FIELD_NAMES.includes(trimmed))
    return `'${trimmed}' is a reserved field name. Pick a different name.`;
  if (existingFields.some((f) => f.name.toLowerCase() === trimmed.toLowerCase()))
    return `A field named '${trimmed}' already exists on this table. Pick a different name.`;
  return null;
}

function loadStates(workflow: WorkflowDefinition | undefined): EditorState[] {
  if (!workflow) return [];
  return workflow.states.map((s, i) => {
    // Round-trip the reverse toggle: a transition pointing at an earlier state.
    const earlierSlugs = new Set(workflow.states.slice(0, i).map((p) => p.slug));
    const allowReverse = (s.transitions ?? []).some((t) => earlierSlugs.has(t.to));
    return {
      id: nextId(),
      slug: s.slug,
      label: s.label,
      category: s.category,
      allowReverse,
      slugEdited: true,
      originalSlug: s.slug,
    };
  });
}

/** Linear transitions from list order + optional reverse to previous active state. */
function buildStates(states: EditorState[]): StateDefinition[] {
  return states.map((s, i) => {
    const transitions: TransitionDefinition[] = [];
    if (s.category === "active") {
      const next = states[i + 1];
      if (next) transitions.push({ to: next.slug, label: `Move to ${next.label}` });
      if (s.allowReverse) {
        for (let j = i - 1; j >= 0; j--) {
          const prev = states[j];
          if (prev && prev.category === "active") {
            transitions.push({ to: prev.slug, label: `Back to ${prev.label}` });
            break;
          }
        }
      }
    }
    return { slug: s.slug, label: s.label, category: s.category, transitions };
  });
}

interface WorkflowEditorModalProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityTypeDefinition;
  onSaved?: () => void;
}

export function WorkflowEditorModal({ open, onClose, entityType, onSaved }: WorkflowEditorModalProps) {
  const [states, setStates] = useState<EditorState[]>([]);
  const [initialId, setInitialId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldName, setFieldName] = useState("status");
  const [fieldNameTouched, setFieldNameTouched] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const update = useUpdateEntityType();

  // A workflow already exists → its field is created and immutable (Q1A):
  // renaming after rows hold state needs a data migration we don't do here.
  const isExisting = !!entityType.workflow;

  useEffect(() => {
    if (!open) return;
    const loaded = loadStates(entityType.workflow);
    setStates(loaded);
    const initial = loaded.find((s) => s.slug === entityType.workflow?.initial_state) ?? loaded[0] ?? null;
    setInitialId(initial?.id ?? null);
    setSelectedId(loaded[0]?.id ?? null);
    setFieldName(entityType.workflow?.field ?? "status");
    setFieldNameTouched(false);
    setDeleteOpen(false);
    setError(null);
  }, [open, entityType.workflow]);

  if (!open) return null;

  const selected = states.find((s) => s.id === selectedId) ?? null;

  function patchState(id: string, patch: Partial<EditorState>) {
    setStates((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function addState() {
    const base = "new_state";
    const taken = new Set(states.map((s) => s.slug));
    let slug = base;
    let n = 1;
    while (taken.has(slug)) slug = `${base}_${++n}`;
    const st: EditorState = {
      id: nextId(),
      slug,
      label: "New state",
      category: "active",
      allowReverse: false,
      slugEdited: false,
      originalSlug: null,
    };
    setStates((prev) => [...prev, st]);
    setSelectedId(st.id);
    if (!initialId) setInitialId(st.id);
  }

  function removeState(id: string) {
    setStates((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (initialId === id) {
        const firstActive = next.find((s) => s.category === "active") ?? next[0];
        setInitialId(firstActive?.id ?? null);
      }
      if (selectedId === id) setSelectedId(next[0]?.id ?? null);
      return next;
    });
  }

  function handleDrop(targetId: string) {
    setOverId(null);
    const from = dragId;
    setDragId(null);
    if (!from || from === targetId) return;
    setStates((prev) => {
      const arr = [...prev];
      const fromIdx = arr.findIndex((s) => s.id === from);
      const toIdx = arr.findIndex((s) => s.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      if (!moved) return prev;
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  }

  // --- validation -----------------------------------------------------------
  // The field name is only editable on initial create; an existing workflow's
  // field is immutable so it never needs (re)validation.
  const fieldNameError = isExisting ? null : validateFieldName(fieldName, entityType.fields);

  const slugCounts = states.reduce<Record<string, number>>((acc, s) => {
    acc[s.slug] = (acc[s.slug] ?? 0) + 1;
    return acc;
  }, {});
  const duplicateSlugs = Object.entries(slugCounts).filter(([, n]) => n > 1).map(([slug]) => slug);
  const initialState = states.find((s) => s.id === initialId) ?? null;
  const validationErrors: string[] = [];
  if (states.length === 0) validationErrors.push("Add at least one state.");
  if (states.some((s) => !s.slug || !s.label.trim())) validationErrors.push("Every state needs a label and slug.");
  if (duplicateSlugs.length > 0) validationErrors.push(`Duplicate slug: ${duplicateSlugs.join(", ")}.`);
  if (states.length > 0 && !initialState) validationErrors.push("Choose an initial state.");
  if (initialState && initialState.category !== "active") validationErrors.push("The initial state must be active.");
  const canSave = validationErrors.length === 0 && !fieldNameError && !update.isPending;

  async function handleSave() {
    if (!canSave || !initialState) return;
    setError(null);
    const slugs = states.map((s) => s.slug);
    // The workflow owns its backing enum field: create it on first save, or keep
    // its choices synced to the state slugs (in order) on subsequent saves.
    let fields: FieldDefinition[];
    if (entityType.fields.some((f) => f.name === fieldName)) {
      fields = entityType.fields.map((f) =>
        f.name === fieldName ? { ...f, type: "enum" as const, choices: slugs } : f,
      );
    } else {
      fields = [...entityType.fields, { name: fieldName, type: "enum", choices: slugs }];
    }
    const workflow: WorkflowDefinition = {
      field: fieldName,
      initial_state: initialState.slug,
      states: buildStates(states),
    };
    try {
      await update.mutateAsync({
        slug: entityType.slug,
        patch: { fields, workflow },
        expectedVersion: entityType._version,
      });
      showToast(entityType.workflow ? "Workflow updated" : "Workflow added");
      onSaved?.();
      onClose();
    } catch (e) {
      const err = e as Error & { fieldErrors?: { message: string }[] };
      setError(err.fieldErrors?.[0]?.message ?? (e instanceof Error ? e.message : "Couldn't save workflow"));
    }
  }

  // Delete the workflow AND its backing field in one update (Q2 — single unit).
  async function handleDeleteWorkflow() {
    const fields = entityType.fields.filter((f) => f.name !== fieldName);
    await update.mutateAsync({
      slug: entityType.slug,
      patch: { fields, workflow: null },
      expectedVersion: entityType._version,
    });
    showToast("Workflow deleted");
    onSaved?.();
    onClose();
  }

  // --- styles ---------------------------------------------------------------
  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    fontFamily: "var(--font-sans)",
  };
  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: "var(--border-radius-lg)",
    width: 600,
    maxWidth: "94vw",
    maxHeight: "84vh",
    display: "flex",
    flexDirection: "column",
  };
  const headerStyle: CSSProperties = {
    padding: "18px 20px",
    borderBottom: "0.5px solid var(--color-border-tertiary)",
  };
  const bodyStyle: CSSProperties = { display: "flex", minHeight: 320, flex: 1, overflow: "hidden" };
  const listPane: CSSProperties = {
    width: 240,
    borderRight: "0.5px solid var(--color-border-tertiary)",
    padding: 12,
    overflowY: "auto",
  };
  const detailPane: CSSProperties = { flex: 1, padding: 18, overflowY: "auto" };
  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  };
  const inputStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "7px 9px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    boxSizing: "border-box",
  };

  return (
    <>
      <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !update.isPending) onClose(); }}>
        <div role="dialog" aria-modal="true" aria-label="Workflow editor" style={modal}>
          <div style={headerStyle}>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
              {isExisting ? "Edit workflow" : "Add workflow"}
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>
              {entityType.name}
            </div>
          </div>

          {/* Sprint 14b — the workflow names and owns its backing enum field. */}
          <div style={{ padding: "14px 20px 0" }}>
            <label style={labelStyle} htmlFor="wf-field-name">Workflow field name</label>
            <input
              id="wf-field-name"
              style={{
                ...inputStyle,
                ...(isExisting
                  ? {
                      background: "var(--color-background-secondary)",
                      color: "var(--color-text-secondary)",
                      cursor: "not-allowed",
                    }
                  : {}),
              }}
              value={fieldName}
              readOnly={isExisting}
              aria-readonly={isExisting}
              onChange={(e) => {
                if (isExisting) return;
                setFieldName(e.target.value);
                if (fieldNameTouched) setError(null);
              }}
              onBlur={() => setFieldNameTouched(true)}
              placeholder="status"
              aria-label="Workflow field name"
            />
            {isExisting ? (
              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 4, lineHeight: 1.4 }}>
                Field name can't be changed after the workflow is created. To rename, delete this workflow and create a new one.
              </div>
            ) : fieldNameTouched && fieldNameError ? (
              <div style={{ fontSize: 11, color: "var(--c-red-600)", marginTop: 4, lineHeight: 1.4 }}>
                {fieldNameError}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 4, lineHeight: 1.4 }}>
                This field will be created on the table to store the current workflow state.
              </div>
            )}
          </div>

          {states.length === 0 ? (
            <div style={{ padding: "28px 24px 36px", textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.6, maxWidth: 380, margin: "0 auto 16px" }}>
                No states yet. Add states to enable stage-based UX — the Stage Stepper on
                row detail and transition-aware inline editing.
              </div>
              <button data-variant="primary" onClick={addState} style={{ fontSize: 13, padding: "8px 14px" }}>
                <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 4 }} aria-hidden="true" />
                Add first state
              </button>
            </div>
          ) : (
            <div style={bodyStyle}>
              <div style={listPane}>
                {states.map((s) => {
                  const isSel = s.id === selectedId;
                  return (
                    <div
                      key={s.id}
                      draggable
                      onDragStart={() => setDragId(s.id)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (overId !== s.id) setOverId(s.id);
                      }}
                      onDragEnd={() => {
                        setDragId(null);
                        setOverId(null);
                      }}
                      onDrop={() => handleDrop(s.id)}
                      onClick={() => setSelectedId(s.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "7px 8px",
                        marginBottom: 4,
                        borderRadius: "var(--border-radius-sm)",
                        cursor: "pointer",
                        background: isSel ? "var(--color-background-secondary)" : "transparent",
                        border: overId === s.id ? "0.5px solid var(--color-text-info)" : "0.5px solid transparent",
                      }}
                    >
                      <i className="ti ti-grip-vertical" style={{ fontSize: 13, opacity: 0.4, cursor: "grab" }} aria-hidden="true" />
                      <span style={{ flex: 1, fontSize: 13, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.label || <span style={{ color: "var(--color-text-tertiary)" }}>Untitled</span>}
                      </span>
                      {s.id === initialId && (
                        <span title="Initial state" style={{ fontSize: 9, fontWeight: 600, color: "var(--c-blue-600)", textTransform: "uppercase" }}>
                          start
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color: s.category === "terminal" ? "var(--color-text-tertiary)" : "var(--c-teal-700)",
                        }}
                      >
                        {s.category === "terminal" ? "end" : "active"}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeState(s.id);
                        }}
                        title="Remove state"
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-tertiary)", padding: 0, display: "inline-flex" }}
                      >
                        <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
                <button
                  onClick={addState}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    width: "100%",
                    marginTop: 6,
                    padding: "7px 8px",
                    border: "0.5px dashed var(--color-border-secondary)",
                    borderRadius: "var(--border-radius-sm)",
                    background: "transparent",
                    color: "var(--color-text-secondary)",
                    cursor: "pointer",
                    fontSize: 12,
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" />
                  Add state
                </button>
              </div>

              <div style={detailPane}>
                {selected ? (
                  <StateDetail
                    state={selected}
                    isInitial={selected.id === initialId}
                    hasPreviousActive={
                      states.slice(0, states.findIndex((s) => s.id === selected.id)).some((s) => s.category === "active")
                    }
                    labelStyle={labelStyle}
                    inputStyle={inputStyle}
                    onChangeLabel={(label) =>
                      patchState(selected.id, { label, ...(selected.slugEdited ? {} : { slug: slugify(label) }) })
                    }
                    onChangeSlug={(slug) => patchState(selected.id, { slug: slugify(slug), slugEdited: true })}
                    onChangeCategory={(category) => patchState(selected.id, { category })}
                    onToggleReverse={(allowReverse) => patchState(selected.id, { allowReverse })}
                    onMakeInitial={() => setInitialId(selected.id)}
                  />
                ) : (
                  <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Select a state to edit.</div>
                )}
              </div>
            </div>
          )}

          <div style={{ padding: "14px 20px", borderTop: "0.5px solid var(--color-border-tertiary)" }}>
            {(error || (states.length > 0 && validationErrors.length > 0)) && (
              <div style={{ fontSize: 12, color: "var(--c-red-600)", marginBottom: 10 }}>
                {error ?? validationErrors[0]}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div>
                {isExisting && (
                  <button
                    onClick={() => setDeleteOpen(true)}
                    disabled={update.isPending}
                    title="Delete workflow"
                    style={{
                      fontSize: 13,
                      padding: "8px 12px",
                      background: "none",
                      border: "none",
                      color: "var(--c-red-600)",
                      cursor: update.isPending ? "not-allowed" : "pointer",
                      fontFamily: "var(--font-sans)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden="true" />
                    Delete workflow
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={onClose} disabled={update.isPending} style={{ fontSize: 13, padding: "8px 14px" }}>
                  Cancel
                </button>
                <button
                  data-variant="primary"
                  onClick={handleSave}
                  disabled={!canSave}
                  style={{ fontSize: 13, padding: "8px 14px" }}
                >
                  {update.isPending ? "Saving..." : "Save workflow"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteWorkflowModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        workflowFieldName={fieldName}
        tableName={entityType.name}
        rowCount={entityType._record_count ?? 0}
        onConfirm={handleDeleteWorkflow}
      />
    </>
  );
}

interface StateDetailProps {
  state: EditorState;
  isInitial: boolean;
  hasPreviousActive: boolean;
  labelStyle: CSSProperties;
  inputStyle: CSSProperties;
  onChangeLabel: (label: string) => void;
  onChangeSlug: (slug: string) => void;
  onChangeCategory: (category: "active" | "terminal") => void;
  onToggleReverse: (allow: boolean) => void;
  onMakeInitial: () => void;
}

function StateDetail({
  state,
  isInitial,
  hasPreviousActive,
  labelStyle,
  inputStyle,
  onChangeLabel,
  onChangeSlug,
  onChangeCategory,
  onToggleReverse,
  onMakeInitial,
}: StateDetailProps) {
  const slugChanged = state.originalSlug !== null && state.slug !== state.originalSlug;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Label</label>
        <input
          style={inputStyle}
          value={state.label}
          onChange={(e) => onChangeLabel(e.target.value)}
          placeholder="e.g. Qualified"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Slug</label>
        <input
          style={inputStyle}
          value={state.slug}
          onChange={(e) => onChangeSlug(e.target.value)}
          placeholder="e.g. qualified"
        />
        {slugChanged && (
          <div style={{ fontSize: 11, color: "var(--color-text-warning)", marginTop: 4, lineHeight: 1.4 }}>
            ⚠ Changing the slug of a saved state can strand existing rows in “{state.originalSlug}”. On save they’re
            recovered to a valid state. Continue only if you mean to.
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Category</label>
        <div style={{ display: "flex", gap: 14 }}>
          {(["active", "terminal"] as const).map((cat) => (
            <label key={cat} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
              <input
                type="radio"
                name="wf-category"
                checked={state.category === cat}
                onChange={() => onChangeCategory(cat)}
              />
              {cat === "active" ? "Active" : "Terminal (end state)"}
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: isInitial ? "default" : "pointer", opacity: isInitial ? 0.6 : 1 }}>
          <input type="radio" name="wf-initial" checked={isInitial} onChange={onMakeInitial} disabled={isInitial} />
          Initial state {isInitial && "(current)"}
        </label>
        <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>
          New rows start here. The initial state must be active.
        </div>
      </div>

      {state.category === "active" && hasPreviousActive && (
        <div style={{ marginBottom: 4 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={state.allowReverse} onChange={(e) => onToggleReverse(e.target.checked)} />
            Allow reverse to the previous active state
          </label>
          <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>
            Adds a back-step transition so a row can move backward from here.
          </div>
        </div>
      )}
    </div>
  );
}
